import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe, Server, Database, Activity, Check, X, Clock, 
  RefreshCw, Download, ExternalLink, Shield, Wifi, WifiOff,
  FileText, Microscope, Pill, Building2, HardDrive, Scan,
  Settings, Save, Trash2, Eye, EyeOff, Key
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const SYSTEM_ICONS = {
  sih: Building2,
  dpi: FileText,
  sante_civ: Globe,
  openellis: Microscope,
  msupply: Pill,
  pacs: Scan
};

const SYSTEM_COLORS = {
  sih: { bg: 'from-blue-600/20 to-blue-800/10', border: 'border-blue-500/30', text: 'text-blue-300', icon: 'bg-blue-500/20' },
  dpi: { bg: 'from-emerald-600/20 to-emerald-800/10', border: 'border-emerald-500/30', text: 'text-emerald-300', icon: 'bg-emerald-500/20' },
  sante_civ: { bg: 'from-orange-600/20 to-orange-800/10', border: 'border-orange-500/30', text: 'text-orange-300', icon: 'bg-orange-500/20' },
  openellis: { bg: 'from-purple-600/20 to-purple-800/10', border: 'border-purple-500/30', text: 'text-purple-300', icon: 'bg-purple-500/20' },
  msupply: { bg: 'from-cyan-600/20 to-cyan-800/10', border: 'border-cyan-500/30', text: 'text-cyan-300', icon: 'bg-cyan-500/20' },
  pacs: { bg: 'from-rose-600/20 to-rose-800/10', border: 'border-rose-500/30', text: 'text-rose-300', icon: 'bg-rose-500/20' }
};

const StatusBadge = ({ configured, hasKey }) => {
  if (configured && hasKey) {
    return (
      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium" data-testid="status-connected">
        <Wifi className="w-3 h-3" /> Connecte
      </span>
    );
  }
  if (configured) {
    return (
      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium" data-testid="status-partial">
        <Clock className="w-3 h-3" /> Cle manquante
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-500/20 text-gray-400 text-xs font-medium" data-testid="status-awaiting">
      <WifiOff className="w-3 h-3" /> En attente
    </span>
  );
};

const SystemCard = ({ system, onRefresh }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [configForm, setConfigForm] = useState({
    api_url: '', api_key: '', api_secret: '', notes: ''
  });
  const Icon = SYSTEM_ICONS[system.id] || Server;
  const colors = SYSTEM_COLORS[system.id] || SYSTEM_COLORS.sih;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/interoperability/hub/${system.id}/test`);
      setTestResult(res.data);
      if (res.data.success) {
        toast({ title: 'Connexion reussie', description: `${system.name} repond correctement` });
      } else {
        toast({ title: 'Echec connexion', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Erreur reseau' });
    } finally {
      setTesting(false);
    }
  };

  const openConfig = async () => {
    try {
      const res = await api.get(`/interoperability/hub/${system.id}/config`);
      const saved = res.data.saved_config;
      if (saved) {
        setConfigForm({
          api_url: saved.api_url || '',
          api_key: '', // Never pre-fill secrets
          api_secret: '',
          notes: saved.notes || ''
        });
      } else {
        setConfigForm({ api_url: '', api_key: '', api_secret: '', notes: '' });
      }
    } catch {
      setConfigForm({ api_url: '', api_key: '', api_secret: '', notes: '' });
    }
    setShowConfig(true);
  };

  const handleSaveConfig = async () => {
    if (!configForm.api_url.trim()) {
      toast({ title: 'Erreur', description: "L'URL de l'API est requise", variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/interoperability/hub/${system.id}/config`, configForm);
      toast({ title: 'Configuration sauvegardee', description: system.name });
      setShowConfig(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Echec sauvegarde', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    setDeleting(true);
    try {
      await api.delete(`/interoperability/hub/${system.id}/config`);
      toast({ title: 'Configuration supprimee', description: system.name });
      setShowConfig(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card 
        className={`p-5 bg-gradient-to-br ${colors.bg} ${colors.border} border transition-all hover:scale-[1.01]`}
        data-testid={`system-card-${system.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${colors.icon} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${colors.text}`} />
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm leading-tight">{system.name}</h4>
              <p className="text-gray-400 text-xs mt-0.5">{system.standard}</p>
            </div>
          </div>
          <StatusBadge configured={system.configured} hasKey={system.has_key} />
        </div>

        <p className="text-gray-300 text-xs mb-3 leading-relaxed">{system.description}</p>

        {/* Endpoints */}
        <div className="mb-3">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1.5">Points de terminaison</p>
          <div className="space-y-1">
            {Object.entries(system.endpoints || {}).map(([key, path]) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${system.configured ? 'bg-green-400' : 'bg-gray-600'}`} />
                <span className="text-gray-400 w-24 truncate">{key}</span>
                <code className="text-gray-500 text-[10px] truncate flex-1">{path}</code>
              </div>
            ))}
          </div>
        </div>

        {/* URL preview when configured */}
        {system.configured && system.url && (
          <div className="p-2 bg-green-900/20 border border-green-500/10 rounded-lg mb-3">
            <p className="text-green-400 text-[10px] uppercase tracking-wider mb-0.5">URL configuree</p>
            <code className="text-green-300 text-xs">{system.url}</code>
            {system.source && <span className="text-gray-500 text-[10px] ml-2">({system.source})</span>}
          </div>
        )}

        {/* Config info when not configured */}
        {!system.configured && (
          <div className="p-2.5 bg-gray-800/50 rounded-lg mb-3">
            <p className="text-gray-400 text-xs">{system.message || 'Non configure'}</p>
          </div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`p-2.5 rounded-lg mb-3 ${testResult.success ? 'bg-green-900/30 border border-green-500/20' : 'bg-red-900/30 border border-red-500/20'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-red-400" />}
              <span className={`text-xs ${testResult.success ? 'text-green-300' : 'text-red-300'}`}>
                {testResult.message}
              </span>
            </div>
            {testResult.response_time_ms && (
              <p className="text-gray-500 text-[10px] mt-1">Temps de reponse: {Math.round(testResult.response_time_ms)}ms</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            onClick={openConfig}
            size="sm"
            className={`flex-1 ${system.configured ? 'bg-green-700 hover:bg-green-800' : 'bg-cyan-700 hover:bg-cyan-800'} text-xs`}
            data-testid={`config-btn-${system.id}`}
          >
            <Settings className="w-3 h-3 mr-1.5" />
            {system.configured ? 'Modifier' : 'Configurer'}
          </Button>
          <Button
            onClick={handleTest}
            disabled={testing}
            size="sm"
            variant="outline"
            className={`flex-1 ${colors.border} ${colors.text} hover:bg-white/5 text-xs`}
            data-testid={`test-btn-${system.id}`}
          >
            {testing ? (
              <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Test...</>
            ) : (
              <><Activity className="w-3 h-3 mr-1.5" /> Tester</>
            )}
          </Button>
        </div>
      </Card>

      {/* Configuration Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-400" />
              Configurer {system.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-gray-300 text-sm">URL de l'API *</Label>
              <Input
                value={configForm.api_url}
                onChange={(e) => setConfigForm({ ...configForm, api_url: e.target.value })}
                placeholder="https://api.example.com/fhir"
                className="bg-gray-800 border-gray-600 text-white mt-1 font-mono text-sm"
                data-testid={`config-url-${system.id}`}
              />
              <p className="text-gray-500 text-[10px] mt-1">L'URL de base du serveur {system.standard}</p>
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Cle API</Label>
              <div className="relative mt-1">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={configForm.api_key}
                  onChange={(e) => setConfigForm({ ...configForm, api_key: e.target.value })}
                  placeholder={system.configured ? '(inchangee si vide)' : 'Bearer token ou API key'}
                  className="bg-gray-800 border-gray-600 text-white pr-10 font-mono text-sm"
                  data-testid={`config-key-${system.id}`}
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Secret API</Label>
              <div className="relative mt-1">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={configForm.api_secret}
                  onChange={(e) => setConfigForm({ ...configForm, api_secret: e.target.value })}
                  placeholder={system.configured ? '(inchange si vide)' : 'Secret (optionnel)'}
                  className="bg-gray-800 border-gray-600 text-white pr-10 font-mono text-sm"
                  data-testid={`config-secret-${system.id}`}
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-sm">Notes</Label>
              <Textarea
                value={configForm.notes}
                onChange={(e) => setConfigForm({ ...configForm, notes: e.target.value })}
                placeholder="Notes internes (contact, date d'expiration cle, etc.)"
                className="bg-gray-800 border-gray-600 text-white mt-1 text-sm min-h-[60px]"
                data-testid={`config-notes-${system.id}`}
              />
            </div>

            {system.configured && (
              <div className="p-3 bg-yellow-900/20 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-300 text-xs">
                  Ce systeme est deja configure. Laissez les champs Cle/Secret vides pour conserver les valeurs actuelles.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            {system.configured && (
              <Button
                onClick={handleDeleteConfig}
                disabled={deleting}
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-500/20"
                data-testid={`config-delete-${system.id}`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {deleting ? 'Suppression...' : 'Supprimer'}
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setShowConfig(false)} className="border-gray-600 text-gray-300">
              Annuler
            </Button>
            <Button
              onClick={handleSaveConfig}
              disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid={`config-save-${system.id}`}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const InteroperabilityHub = () => {
  const [hubData, setHubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snomedSearch, setSnomedSearch] = useState('');
  const [snomedResults, setSnomedResults] = useState([]);
  const [snomedSearching, setSnomedSearching] = useState(false);
  const [snomedMapping, setSnomedMapping] = useState(null);
  const [dhis2Data, setDhis2Data] = useState(null);
  const [dhis2Loading, setDhis2Loading] = useState(false);

  const fetchHub = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/interoperability/hub/status');
      setHubData(res.data);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger le hub', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHub(); }, [fetchHub]);

  const handleSnomedSearch = async () => {
    if (!snomedSearch.trim()) return;
    setSnomedSearching(true);
    try {
      const res = await api.get(`/interoperability/snomed/lookup?term=${encodeURIComponent(snomedSearch)}&limit=10`);
      setSnomedResults(res.data.results || []);
      if (res.data.results?.length === 0) {
        toast({ title: 'Aucun resultat', description: 'Essayez un autre terme' });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: 'Recherche SNOMED echouee', variant: 'destructive' });
    } finally {
      setSnomedSearching(false);
    }
  };

  const loadSnomedMapping = async () => {
    try {
      const res = await api.get('/interoperability/snomed/mapping');
      setSnomedMapping(res.data);
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const loadDhis2Export = async () => {
    setDhis2Loading(true);
    try {
      const res = await api.get('/interoperability/export/dhis2');
      setDhis2Data(res.data);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Export DHIS2 echoue', variant: 'destructive' });
    } finally {
      setDhis2Loading(false);
    }
  };

  const downloadDhis2CSV = async () => {
    try {
      const response = await api.get('/interoperability/export/dhis2/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fleurdevie_dhis2_${new Date().toISOString().slice(0, 7)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'CSV telecharge' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Telechargement echoue', variant: 'destructive' });
    }
  };

  const exportHubSummary = async () => {
    try {
      const res = await api.get('/interoperability/hub/export-summary');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleurdevie_interop_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast({ title: 'Rapport exporte' });
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
        <span className="text-gray-400 ml-3">Chargement du hub...</span>
      </div>
    );
  }

  const systems = hubData?.systems ? Object.values(hubData.systems) : [];
  const summary = hubData?.summary || {};
  const existing = hubData?.existing_integrations || {};

  return (
    <div className="space-y-6" data-testid="interoperability-hub">
      {/* Hub Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Hub d'Interoperabilite
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Connexions avec les systemes de sante nationaux et internationaux
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportHubSummary} variant="outline" size="sm" className="border-cyan-600 text-cyan-300 hover:bg-cyan-600/20" data-testid="export-hub-btn">
            <Download className="w-4 h-4 mr-1.5" /> Exporter
          </Button>
          <Button onClick={fetchHub} variant="outline" size="sm" className="border-gray-600 text-gray-300" data-testid="refresh-hub-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-cyan-900/20 border-cyan-500/20">
          <p className="text-cyan-300 text-xs uppercase tracking-wider">Systemes</p>
          <p className="text-white text-2xl font-bold mt-1">{summary.total || 0}</p>
          <p className="text-cyan-400 text-xs">{summary.configured || 0} configures</p>
        </Card>
        <Card className="p-4 bg-green-900/20 border-green-500/20">
          <div className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-green-400" />
            <p className="text-green-300 text-xs uppercase tracking-wider">SNOMED CT</p>
          </div>
          <p className="text-white text-2xl font-bold mt-1">{existing.snomed_ct?.symptoms_mapped || 0}</p>
          <p className="text-green-400 text-xs">symptomes codes</p>
        </Card>
        <Card className="p-4 bg-purple-900/20 border-purple-500/20">
          <div className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-purple-400" />
            <p className="text-purple-300 text-xs uppercase tracking-wider">DHIS2</p>
          </div>
          <p className="text-white text-2xl font-bold mt-1">v2.40+</p>
          <p className="text-purple-400 text-xs">Export actif</p>
        </Card>
        <Card className="p-4 bg-orange-900/20 border-orange-500/20">
          <p className="text-orange-300 text-xs uppercase tracking-wider">En attente</p>
          <p className="text-white text-2xl font-bold mt-1">{summary.awaiting || 0}</p>
          <p className="text-orange-400 text-xs">cles Ministere</p>
        </Card>
      </div>

      {/* Active Integrations - SNOMED & DHIS2 */}
      <Card className="p-5 bg-gray-800 border-gray-700">
        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-400" />
          Integrations actives
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* SNOMED CT */}
          <div className="p-4 bg-green-900/20 border border-green-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-green-400" />
                <span className="text-white font-semibold">SNOMED CT</span>
              </div>
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Actif</span>
            </div>
            <p className="text-gray-400 text-xs mb-3">Codage clinique international des symptomes et contraceptions</p>
            
            {/* SNOMED Search */}
            <div className="flex gap-2 mb-3">
              <input
                value={snomedSearch}
                onChange={(e) => setSnomedSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSnomedSearch()}
                placeholder="Rechercher un concept SNOMED..."
                className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-green-500"
                data-testid="snomed-search-input"
              />
              <Button onClick={handleSnomedSearch} disabled={snomedSearching} size="sm" className="bg-green-600 hover:bg-green-700 text-xs" data-testid="snomed-search-btn">
                {snomedSearching ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Chercher'}
              </Button>
            </div>

            {snomedResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
                {snomedResults.map((concept) => (
                  <div key={concept.code} className="flex items-center gap-2 p-1.5 bg-gray-800/50 rounded text-xs">
                    <code className="text-green-400 font-mono">{concept.code}</code>
                    <span className="text-white truncate">{concept.preferred || concept.display}</span>
                  </div>
                ))}
              </div>
            )}

            <Button onClick={loadSnomedMapping} variant="outline" size="sm" className="w-full border-green-600 text-green-300 hover:bg-green-600/20 text-xs" data-testid="snomed-mapping-btn">
              <FileText className="w-3 h-3 mr-1.5" /> Voir le mapping Fleur de Vie
            </Button>
          </div>

          {/* DHIS2 */}
          <div className="p-4 bg-purple-900/20 border border-purple-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-purple-400" />
                <span className="text-white font-semibold">DHIS2</span>
              </div>
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Actif</span>
            </div>
            <p className="text-gray-400 text-xs mb-3">Export des donnees agregees au format dataValueSets (OMS/Ministere)</p>

            <div className="flex gap-2 mb-3">
              <Button onClick={loadDhis2Export} disabled={dhis2Loading} size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700 text-xs" data-testid="dhis2-preview-btn">
                {dhis2Loading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Activity className="w-3 h-3 mr-1" />}
                Apercu des donnees
              </Button>
              <Button onClick={downloadDhis2CSV} variant="outline" size="sm" className="flex-1 border-purple-600 text-purple-300 hover:bg-purple-600/20 text-xs" data-testid="dhis2-csv-btn">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>

            {dhis2Data && (
              <div className="max-h-40 overflow-y-auto space-y-1">
                <p className="text-gray-500 text-[10px] mb-1">
                  Periode: {dhis2Data.metadata?.period} | {dhis2Data.metadata?.totalDataElements} elements
                </p>
                {dhis2Data.dataValueSets?.dataValues?.slice(0, 6).map((dv, idx) => (
                  <div key={idx} className="flex items-center justify-between p-1.5 bg-gray-800/50 rounded text-xs">
                    <span className="text-purple-300 font-mono text-[10px] truncate w-40">{dv.dataElement}</span>
                    <span className="text-white font-bold">{dv.value}</span>
                  </div>
                ))}
                {(dhis2Data.dataValueSets?.dataValues?.length || 0) > 6 && (
                  <p className="text-gray-500 text-[10px] text-center">
                    + {dhis2Data.dataValueSets.dataValues.length - 6} autres elements...
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SNOMED Mapping Display */}
        {snomedMapping && (
          <div className="p-4 bg-gray-700/30 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-white font-semibold text-sm">Mapping SNOMED CT - Fleur de Vie</h5>
              <Button onClick={() => setSnomedMapping(null)} variant="ghost" size="sm" className="text-gray-400 h-6 w-6 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-green-400 text-xs font-semibold mb-2">Symptomes ({Object.keys(snomedMapping.symptoms || {}).length})</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(snomedMapping.symptoms || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs p-1 bg-gray-800/50 rounded">
                      <code className="text-green-400 font-mono text-[10px] w-20">{val.code}</code>
                      <span className="text-white truncate">{val.display}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-purple-400 text-xs font-semibold mb-2">Contraception ({Object.keys(snomedMapping.contraception || {}).length})</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(snomedMapping.contraception || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs p-1 bg-gray-800/50 rounded">
                      <code className="text-purple-400 font-mono text-[10px] w-20">{val.code}</code>
                      <span className="text-white truncate">{val.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-[10px] mt-2 text-center">
              Source: {snomedMapping.metadata?.source} | Version: {snomedMapping.metadata?.snomedVersion}
            </p>
          </div>
        )}
      </Card>

      {/* External Systems Grid */}
      <div>
        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-cyan-400" />
          Systemes externes
          <span className="text-gray-500 text-sm font-normal ml-2">
            En attente des cles du Ministere de la Sante
          </span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systems.map((system) => (
            <SystemCard key={system.id} system={system} onRefresh={fetchHub} />
          ))}
        </div>
      </div>

      {/* Compliance footer */}
      <Card className="p-4 bg-gray-800/50 border-gray-700/50">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold">Conformite & Securite</p>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">
              Toutes les donnees exportees sont anonymisees. Conformite RGPD et Loi CI 2013-450 relative a la protection des donnees a caractere personnel.
              Tous les acces au hub sont journalises et audites. Les connexions aux systemes externes utilisent le chiffrement TLS 1.3.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InteroperabilityHub;
